<template>
  <div>
    <el-dialog
      :title="`タグ管理 (${totalTagCount}/${TAG_MAX_COUNT})`"
      :visible="visible"
      width="800px"
      custom-class="with-title tag-management-dialog"
      @update:visible="$emit('close')"
      @close="handleClose"
    >
      <div class="tag-management-content">
        <!-- アクションエリア（新規追加ボタン or 入力フォーム） -->
        <div class="tag-management-header">
          <div v-if="isAddingNewTag" class="tag-management-form">
            <div class="tag-management-form__input">
              <el-input
                ref="newTagInputRef"
                v-model="newTagInputName"
                size="small"
                placeholder="タグ名を入力（最大20文字）"
                maxlength="20"
              />
              <div class="char-count">{{ newTagInputName.length }}/20</div>
            </div>
            <div class="tag-management-form__actions">
              <el-button size="small" @click="cancelNewTag">キャンセル</el-button>
              <el-button type="primary" size="small" :disabled="!canCreate" @click="saveNewTag">保存</el-button>
            </div>
          </div>
          <template v-else>
            <el-button type="primary" size="small" :disabled="isTagLimitReached" @click="startAddNewTag">
              <i class="el-icon-plus"></i> 新規追加
            </el-button>
            <span class="tag-management-count">タグの登録数 {{ totalTagCount }} / {{ TAG_MAX_COUNT }}</span>
          </template>
        </div>

        <!-- 上限到達時の警告 -->
        <Message v-if="isTagLimitReached" type="warning">
          タグの登録上限（{{ TAG_MAX_COUNT }}件）に達しました。新しいタグを作成するには、既存のタグを削除してください。
        </Message>

        <!-- タグ一覧（ヘッダー・ページネーションは固定し、本体のみスクロールする） -->
        <Message v-if="totalTagCount === 0" type="info">
          タグがありません。「新規追加」からタグを作成してください。
        </Message>
        <template v-else>
          <el-table :data="pagedTags" class="tag-management-table" height="100%">
            <el-table-column label="タグ名" min-width="220">
              <template #default="{ row }">
                <div v-if="editingTagId === row.id" class="tag-edit-cell">
                  <el-input v-model="editingTagName" size="small" placeholder="タグ名" maxlength="20" />
                  <div class="char-count">{{ editingTagName.length }}/20</div>
                </div>
                <span v-else>{{ row.name }}</span>
              </template>
            </el-table-column>
            <el-table-column
              width="200"
              align="right"
              class-name="tag-management-actions"
              label-class-name="tag-management-actions"
            >
              <template #default="{ row }">
                <template v-if="editingTagId === row.id">
                  <el-button size="mini" @click="cancelEditTag">キャンセル</el-button>
                  <el-button size="mini" type="primary" :disabled="!canSaveEdit" @click="saveEditTag">保存</el-button>
                </template>
                <template v-else>
                  <el-button size="mini" :disabled="isAddingNewTag || editingTagId !== null" @click="startEditTag(row)">
                    <i class="el-icon-edit"></i> 編集
                  </el-button>
                  <el-button
                    size="mini"
                    type="danger"
                    :disabled="isAddingNewTag || editingTagId !== null"
                    @click="confirmDeleteTag(row)"
                  >
                    <i class="el-icon-delete"></i> 削除
                  </el-button>
                </template>
              </template>
            </el-table-column>
          </el-table>
          <div class="tag-management-pagination">
            <AppPagination
              :total="totalTagCount"
              :page-size="tagPageSize"
              :current-page="tagPage"
              @paging="handleTagPaging"
            />
          </div>
        </template>
      </div>
    </el-dialog>

    <!-- タグ削除確認ダイアログ -->
    <el-dialog
      :title="pendingDeleteTag ? `「${pendingDeleteTag.name}」を削除しますか？` : ''"
      :visible="pendingDeleteTag !== null"
      width="480px"
      append-to-body
      custom-class="tag-delete-dialog"
      @update:visible="cancelDeleteTag"
      @close="cancelDeleteTag"
    >
      <div class="tag-delete-dialog-content">
        <Message type="danger">
          <div>このタグが付与されている{{ pendingDeleteTagPostCount }}件の投稿からタグが外れます。</div>
          <div>この操作は取り消しできません。</div>
        </Message>
      </div>
      <template slot="footer">
        <div class="tag-delete-dialog-footer">
          <el-button type="danger" @click="executeDeleteTag">削除</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, ref, watch } from 'vue'

import AppPagination from '@/client/components/_atoms/app_pagination/Component.vue'
import { PropPageSizeType } from '@/client/components/_atoms/app_pagination/types'
import Message from '@/client/components/_atoms/Message.vue'
import { TrackingService } from '@/client/services'
import { TAG_TOTAL_MAX } from '@/typings/twitter_benchmark_campaign_posts'

import { useTagManagement } from '../../../hooks/useTagManagement'

const TAG_MAX_COUNT = TAG_TOTAL_MAX

/**
 * タグ管理ダイアログ（CRUD は useTagManagement 経由で呼び、成功後の一覧同期はストア側が担う）。
 */
export default defineComponent({
  name: 'TwBenchmarkTagManagementDialog',

  components: {
    Message,
    AppPagination
  },

  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },

  emits: ['close'],

  setup(props) {
    const {
      pagedTags,
      totalCount,
      currentPage,
      pageSize,
      confirmingTagId,
      fetch,
      create,
      rename,
      askDelete,
      cancelDelete,
      confirmDelete
    } = useTagManagement()

    const isAddingNewTag = ref(false)
    const newTagInputName = ref('')
    const newTagInputRef = ref<{ focus?: () => void } | null>(null)
    const editingTagId = ref<number | null>(null)
    const editingTagName = ref('')

    const totalTagCount = computed(() => totalCount.value)
    const tagPage = computed(() => currentPage.value)
    const tagPageSize = computed(() => pageSize.value as PropPageSizeType)
    const isTagLimitReached = computed(() => totalTagCount.value >= TAG_MAX_COUNT)

    const pendingDeleteTag = computed(() => pagedTags.value.find(tag => tag.id === confirmingTagId.value) ?? null)
    const pendingDeleteTagPostCount = computed(() => pendingDeleteTag.value?.assignedCount ?? 0)

    const canCreate = computed(() => {
      const trimmed = newTagInputName.value.trim()
      return trimmed.length >= 1 && trimmed.length <= 20
    })

    const canSaveEdit = computed(() => {
      const trimmed = editingTagName.value.trim()
      return trimmed.length >= 1 && trimmed.length <= 20
    })

    const resetTransientState = (): void => {
      isAddingNewTag.value = false
      newTagInputName.value = ''
      editingTagId.value = null
      editingTagName.value = ''
      cancelDelete()
    }

    watch(
      () => props.visible,
      isOpen => {
        if (isOpen) {
          resetTransientState()
          void fetch(1, tagPageSize.value)
        }
      },
      { immediate: true }
    )

    const handleClose = (): void => {
      resetTransientState()
    }

    const startAddNewTag = (): void => {
      if (isTagLimitReached.value) return
      TrackingService.sendEvent('click:競合キャンペーン分析|タグ管理|新規追加')
      isAddingNewTag.value = true
      newTagInputName.value = ''
      void nextTick(() => newTagInputRef.value?.focus?.())
    }

    const cancelNewTag = (): void => {
      isAddingNewTag.value = false
      newTagInputName.value = ''
    }

    const saveNewTag = async (): Promise<void> => {
      if (!canCreate.value || isTagLimitReached.value) return

      TrackingService.sendEvent('click:競合キャンペーン分析|タグ管理|新規追加|保存')
      const ok = await create(newTagInputName.value.trim())
      if (!ok) return

      isAddingNewTag.value = false
      newTagInputName.value = ''

      const lastPage = Math.max(1, Math.ceil(totalTagCount.value / tagPageSize.value))
      await fetch(lastPage, tagPageSize.value)
    }

    const startEditTag = (tag: { id: number; name: string }): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|タグ管理|編集')
      editingTagId.value = tag.id
      editingTagName.value = tag.name
    }

    const cancelEditTag = (): void => {
      editingTagId.value = null
      editingTagName.value = ''
    }

    const saveEditTag = async (): Promise<void> => {
      const targetId = editingTagId.value
      if (targetId === null || !canSaveEdit.value) return

      TrackingService.sendEvent('click:競合キャンペーン分析|タグ管理|編集|保存')
      const ok = await rename(targetId, editingTagName.value.trim())
      if (ok) cancelEditTag()
    }

    const confirmDeleteTag = (tag: { id: number }): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|タグ管理|削除')
      askDelete(tag.id)
    }

    const cancelDeleteTag = (): void => {
      cancelDelete()
    }

    const executeDeleteTag = async (): Promise<void> => {
      TrackingService.sendEvent('click:競合キャンペーン分析|タグ管理|削除|実行')
      await confirmDelete()
    }

    const handleTagPaging = ({
      currentPage: nextPage,
      pageSize: nextPageSize
    }: {
      currentPage: number
      pageSize: PropPageSizeType
    }): void => {
      const actionPaging = nextPage === tagPage.value + 1 ? '|次へ' : nextPage === tagPage.value - 1 ? '|前へ' : ''
      TrackingService.sendEvent(`click:競合キャンペーン分析|タグ管理|ページネーション${actionPaging}`)
      void fetch(nextPage, nextPageSize)
    }

    return {
      TAG_MAX_COUNT,
      pagedTags,
      totalTagCount,
      isTagLimitReached,
      tagPage,
      tagPageSize,
      isAddingNewTag,
      newTagInputName,
      newTagInputRef,
      editingTagId,
      editingTagName,
      pendingDeleteTag,
      pendingDeleteTagPostCount,
      canCreate,
      canSaveEdit,
      handleClose,
      startAddNewTag,
      cancelNewTag,
      saveNewTag,
      startEditTag,
      cancelEditTag,
      saveEditTag,
      confirmDeleteTag,
      cancelDeleteTag,
      executeDeleteTag,
      handleTagPaging
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

:deep(.tag-management-dialog) {
  .el-dialog__body {
    height: 70vh;
    overflow: hidden;
  }
}

.tag-management-content {
  display: flex;
  flex-direction: column;
  height: calc(70vh - #{$spacing-unit * 4});

  .tag-management-header {
    display: flex;
    flex-shrink: 0;
    gap: $spacing-unit;
    align-items: center;
    justify-content: space-between;
    min-height: 32px;
    margin-bottom: $spacing-unit * 2;
  }

  .tag-management-count {
    font-size: calc(#{$font-small-size} + 2px);
    color: $sub-text-color;
  }

  .tag-management-pagination {
    display: flex;
    flex-shrink: 0;
    justify-content: flex-end;
    margin-top: $spacing-unit * 2;
  }

  .tag-management-form {
    display: flex;
    flex: 1;
    gap: $spacing-unit;
    align-items: flex-start;

    &__input {
      flex: 1;
    }

    &__actions {
      display: flex;
      flex-shrink: 0;
      gap: $spacing-unit * 0.5;
    }
  }

  .char-count {
    margin-top: 2px;
    font-size: $font-small-size;
    color: $sub-text-color;
    text-align: right;
  }

  .tag-edit-cell {
    display: flex;
    gap: $spacing-unit;
    align-items: center;

    :deep(.el-input) {
      flex: 1;
      max-width: 240px;
    }

    .char-count {
      margin-top: 0;
      white-space: nowrap;
    }
  }
}

.tag-management-table {
  flex: 1;
  width: 100%;
  min-height: 0;

  :deep(.tag-management-actions) {
    .cell {
      padding-right: $spacing-unit * 2;
    }
  }
}

.tag-delete-dialog-content {
  padding-bottom: $spacing-unit;
}

.tag-delete-dialog-footer {
  display: flex;
  justify-content: flex-end;
}
</style>
